import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { is } from 'immer/dist/internal'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import {
  useMode,
  useSelectFirstAndOnlyOption,
} from '../../../../../../../hooks'
import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { FormValues } from '../common'

interface FormProps {
  formik: FormikProps<FormValues>
  isFormFieldValid: any
  getFormErrorMessage: any
  config: any
  isRequestRunning: boolean
  handleClose: () => void
}

export const EditPackagingDataView: React.FC<FormProps> = ({
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  config,
  isRequestRunning,
  handleClose,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const mode = useMode()

  const isInfoMode = mode === 'info'
  const isEditMode = mode === 'edit'

  // PackagingDataId
  const packagingDataIdLabel = (
    <label
      htmlFor="packagingDataId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('packagingDataId'),
      })}
    ></label>
  )

  const packagingDataIdComponent = (
    <InputText
      id="packagingDataId"
      className="w-full"
      value={formik.values.packagingDataId}
      onChange={formik.handleChange}
      disabled={isRequestRunning || isInfoMode || isEditMode}
    />
  )

  const packagingDataIdErrorMsg = (
    <div>{getFormErrorMessage('packagingDataId')}</div>
  )

  const packagingDataIdWrapper = (
    <ConfigurableComponent
      config={config}
      component={packagingDataIdComponent}
      label={packagingDataIdLabel}
      errorMsg={packagingDataIdErrorMsg}
    />
  )

  // Description
  const descriptionLabel = (
    <label
      htmlFor="description"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('description'),
      })}
    ></label>
  )

  const descriptionComponent = (
    <InputText
      id="description"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('description'),
      })}
      value={formik.values.description}
      onChange={formik.handleChange}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const descriptionErrorMsg = <div>{getFormErrorMessage('description')}</div>

  const descriptionWrapper = (
    <ConfigurableComponent
      config={config}
      component={descriptionComponent}
      label={descriptionLabel}
      errorMsg={descriptionErrorMsg}
    />
  )

  // PackagingMaterial
  const packagingMaterialLabel = (
    <label
      htmlFor="packagingMaterial"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('packagingMaterial'),
      })}
    ></label>
  )

  const packagingMaterialComponent = (
    <Dropdown
      id="packagingMaterial"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('packagingMaterial'),
      })}
      value={formik.values.packagingMaterial}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      showClear
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const packagingMaterialErrorMsg = (
    <div>{getFormErrorMessage('packagingMaterial')}</div>
  )

  const packagingMaterialWrapper = (
    <ConfigurableComponent
      config={config}
      component={packagingMaterialComponent}
      label={packagingMaterialLabel}
      errorMsg={packagingMaterialErrorMsg}
    />
  )

  // Length
  const lengthLabel = (
    <label
      htmlFor="length"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('length'),
      })}
    ></label>
  )

  const lengthComponent = (
    <InputNumber
      id="length"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('length'),
      })}
      value={formik.values.length}
      onChange={e => formik.setFieldValue('length', e.value)}
      locale={language}
      minFractionDigits={2}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const lengthErrorMsg = <div>{getFormErrorMessage('length')}</div>

  const lengthWrapper = (
    <ConfigurableComponent
      config={config}
      component={lengthComponent}
      label={lengthLabel}
      errorMsg={lengthErrorMsg}
    />
  )

  // Width
  const widthLabel = (
    <label
      htmlFor="width"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('width'),
      })}
    ></label>
  )

  const widthComponent = (
    <InputNumber
      id="width"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('width'),
      })}
      value={formik.values.width}
      onChange={e => formik.setFieldValue('width', e.value)}
      locale={language}
      minFractionDigits={2}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const widthErrorMsg = <div>{getFormErrorMessage('width')}</div>

  const widthWrapper = (
    <ConfigurableComponent
      config={config}
      component={widthComponent}
      label={widthLabel}
      errorMsg={widthErrorMsg}
    />
  )

  // Height
  const heightLabel = (
    <label
      htmlFor="height"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('height'),
      })}
    ></label>
  )

  const heightComponent = (
    <InputNumber
      id="height"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('height'),
      })}
      value={formik.values.height}
      onChange={e => formik.setFieldValue('height', e.value)}
      locale={language}
      minFractionDigits={2}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const heightErrorMsg = <div>{getFormErrorMessage('height')}</div>

  const heightWrapper = (
    <ConfigurableComponent
      config={config}
      component={heightComponent}
      label={heightLabel}
      errorMsg={heightErrorMsg}
    />
  )

  // MeasurementsUnit
  const measurementsUnitLabel = (
    <label
      htmlFor="measurementsUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('measurementsUnit'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'measurementsUnit',
    config.measurementsUnit.options,
    formik,
  )

  const measurementsUnitComponent = (
    <Dropdown
      id="measurementsUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('measurementsUnit'),
      })}
      value={formik.values.measurementsUnit}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const measurementsUnitErrorMsg = (
    <div>{getFormErrorMessage('measurementsUnit')}</div>
  )

  const measurementsUnitWrapper = (
    <ConfigurableComponent
      config={config}
      component={measurementsUnitComponent}
      label={measurementsUnitLabel}
      errorMsg={measurementsUnitErrorMsg}
    />
  )

  // TareWeight
  const tareWeightLabel = (
    <label
      htmlFor="tareWeight"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('tareWeight'),
      })}
    ></label>
  )

  const tareWeightComponent = (
    <InputNumber
      id="tareWeight"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('tareWeight'),
      })}
      value={formik.values.tareWeight}
      onChange={e => formik.setFieldValue('tareWeight', e.value)}
      locale={language}
      minFractionDigits={3}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const tareWeightErrorMsg = <div>{getFormErrorMessage('tareWeight')}</div>

  const tareWeightWrapper = (
    <ConfigurableComponent
      config={config}
      component={tareWeightComponent}
      label={tareWeightLabel}
      errorMsg={tareWeightErrorMsg}
    />
  )

  // TareWeightUnit
  const tareWeightUnitLabel = (
    <label
      htmlFor="tareWeightUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('tareWeightUnit'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'tareWeightUnit',
    config.tareWeightUnit.options,
    formik,
  )

  const tareWeightUnitComponent = (
    <Dropdown
      id="tareWeightUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('tareWeightUnit'),
      })}
      value={formik.values.tareWeightUnit}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const tareWeightUnitErrorMsg = (
    <div>{getFormErrorMessage('tareWeightUnit')}</div>
  )

  const tareWeightUnitWrapper = (
    <ConfigurableComponent
      config={config}
      component={tareWeightUnitComponent}
      label={tareWeightUnitLabel}
      errorMsg={tareWeightUnitErrorMsg}
    />
  )

  // Volume
  const volumeLabel = (
    <label
      htmlFor="volume"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('volume'),
      })}
    ></label>
  )

  const volumeComponent = (
    <InputNumber
      id="volume"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('volume'),
      })}
      value={formik.values.volume}
      onChange={e => formik.setFieldValue('volume', e.value)}
      locale={language}
      minFractionDigits={3}
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const volumeErrorMsg = <div>{getFormErrorMessage('volume')}</div>

  const volumeWrapper = (
    <ConfigurableComponent
      config={config}
      component={volumeComponent}
      label={volumeLabel}
      errorMsg={volumeErrorMsg}
    />
  )

  // VolumeUnit
  const volumeUnitLabel = (
    <label
      htmlFor="volumeUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('volumeUnit'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption('volumeUnit', config.volumeUnit.options, formik)

  const volumeUnitComponent = (
    <Dropdown
      id="volumeUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('volumeUnit'),
      })}
      value={formik.values.volumeUnit}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const volumeUnitErrorMsg = <div>{getFormErrorMessage('volumeUnit')}</div>

  const volumeUnitWrapper = (
    <ConfigurableComponent
      config={config}
      component={volumeUnitComponent}
      label={volumeUnitLabel}
      errorMsg={volumeUnitErrorMsg}
    />
  )

  // PackagingIdentifier
  const packagingIdentifierLabel = (
    <label
      htmlFor="packagingIdentifier"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('packagingIdentifier'),
      })}
    ></label>
  )

  const handlePackagingIdentifierChange = (event: DropdownChangeEvent) => {
    const value = event.value

    formik.setFieldValue('packagingIdentifier', value)
    if (value === 'disposablePackaging') {
      formik.setFieldValue('propertyIdentifier', undefined)
    }
  }

  const packagingIdentifierComponent = (
    <Dropdown
      id="packagingIdentifier"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('packagingIdentifier'),
      })}
      value={formik.values.packagingIdentifier}
      onChange={handlePackagingIdentifierChange}
      optionLabel="code"
      optionValue="code"
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const packagingIdentifierErrorMsg = (
    <div>{getFormErrorMessage('packagingIdentifier')}</div>
  )

  const packagingIdentifierWrapper = (
    <ConfigurableComponent
      config={config}
      component={packagingIdentifierComponent}
      label={packagingIdentifierLabel}
      errorMsg={packagingIdentifierErrorMsg}
    />
  )

  // PropertyIdentifier
  const propertyIdentifierLabel = (
    <label
      htmlFor="propertyIdentifier"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('propertyIdentifier'),
      })}
    ></label>
  )

  const propertyIdentifierComponent = (
    <Dropdown
      id="propertyIdentifier"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('propertyIdentifier'),
      })}
      value={formik.values.propertyIdentifier}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
      disabled={isRequestRunning || isInfoMode}
    />
  )

  const propertyIdentifierErrorMsg = (
    <div>{getFormErrorMessage('propertyIdentifier')}</div>
  )

  const propertyIdentifierWrapper = (
    <ConfigurableComponent
      config={config}
      component={propertyIdentifierComponent}
      label={propertyIdentifierLabel}
      errorMsg={propertyIdentifierErrorMsg}
      isRequired={formik.values.packagingIdentifier === 'reusablePackaging'}
    />
  )

  // IsStackable
  const isStackableLabel = (
    <label
      htmlFor="isStackable"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('isStackable'),
      })}
    ></label>
  )

  const isStackableComponent = (
    <div className="flex align-items-center" id="isStackable" data-checkbox>
      <Checkbox
        id="isStackable"
        inputId="isStackable"
        name="isStackable"
        value={formik.values.isStackable}
        onChange={formik.handleChange}
        checked={formik.values.isStackable || false}
        className={classNames({
          'p-invalid': !isFormFieldValid('isStackable'),
        })}
        disabled={isRequestRunning || isInfoMode}
      />
      <label htmlFor="isStackable" className="ml-2">
        {t(
          'masterDataManagement.packagingData.table.edit.dialog.form.isStackable.checkbox.label',
        )}
      </label>
    </div>
  )

  const isStackableErrorMsg = <div>{getFormErrorMessage('isStackable')}</div>

  const isStackableWrapper = (
    <ConfigurableComponent
      config={config}
      component={isStackableComponent}
      label={isStackableLabel}
      errorMsg={isStackableErrorMsg}
    />
  )

  // Footer
  const getFooter = () => {
    if (isInfoMode)
      return (
        <Button
          label={t(
            'masterDataManagement.packagingData.table.info.dialog.close.label',
          )}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
      )

    const saveButtonLabel =
      mode === 'add'
        ? t('masterDataManagement.packagingData.table.add.dialog.add.label')
        : t('masterDataManagement.packagingData.table.edit.dialog.save.label')

    return (
      <>
        <Button
          label={t(
            'masterDataManagement.packagingData.table.edit.dialog.cancel.label',
          )}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
        <Button
          label={saveButtonLabel}
          icon={'pi pi-check'}
          className="w-full md:w-auto"
          onClick={formik.submitForm}
          type="submit"
          disabled={!formik.dirty}
          loading={isRequestRunning}
          visible={!isInfoMode}
        />
      </>
    )
  }

  const getHeader = () => {
    if (mode === 'add')
      return t('masterDataManagement.packagingData.table.add.dialog.title')

    if (mode === 'edit')
      return t('masterDataManagement.packagingData.table.edit.dialog.title')

    if (mode === 'info')
      return t('masterDataManagement.packagingData.table.info.dialog.title')
  }

  return (
    <Dialog
      header={getHeader}
      visible
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={getFooter}
      onHide={handleClose}
      blockScroll
    >
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{packagingDataIdWrapper}</div>

        <div className="col-12 md:col-6">{descriptionWrapper}</div>

        <div className="col-12 md:col-6">{packagingMaterialWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-3">{lengthWrapper}</div>

        <div className="col-12 md:col-3">{widthWrapper}</div>

        <div className="col-12 md:col-3">{heightWrapper}</div>

        <div className="col-12 md:col-3">{measurementsUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{tareWeightWrapper}</div>

        <div className="col-12 md:col-6">{tareWeightUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{volumeWrapper}</div>

        <div className="col-12 md:col-6">{volumeUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{packagingIdentifierWrapper}</div>

        {formik.values.packagingIdentifier === 'reusablePackaging' && (
          <div className="col-12 md:col-6">{propertyIdentifierWrapper}</div>
        )}
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{isStackableWrapper}</div>
      </div>
    </Dialog>
  )
}
